import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import AuthContext from '../../context/auth.context';
import {
  OperatorType,
  employeeTypeOptions,
  normalizeEmployeeUsername,
  operatorTypeLabel,
  readOperatorId,
  readOperatorTypeId,
} from '../../types/OperatorTypes';
import '../business/Business.css';
import './UpsertEmployee.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';

function UpsertEmployee() {
  const navigate = useNavigate();
  const location = useLocation();
  const { employeeId } = useParams();
  const isEdit = !!employeeId;
  const listPath = '/employees';

  const auth = React.useContext(AuthContext);
  const operatorTypeId = readOperatorTypeId(auth?.token);
  const operatorId = readOperatorId(auth?.token);
  const isSelf = isEdit && operatorId != null && String(employeeId) === String(operatorId);
  const lockSelfType = isSelf && operatorTypeId === OperatorType.MANAGER;
  const { loading, requestWithMeta } = useHttp();

  const [name, setName] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [typeId, setTypeId] = React.useState(String(OperatorType.EMPLOYEE));
  const [isOwner, setIsOwner] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [isPrefillLoading, setIsPrefillLoading] = React.useState(false);

  const applyEmployee = React.useCallback((employee) => {
    const owner = Number(employee?.typeId) === OperatorType.OWNER;
    setIsOwner(owner);
    setName(employee?.name ?? '');
    setUsername(employee?.username ?? '');
    setTypeId(String(owner ? OperatorType.OWNER : (employee?.typeId ?? OperatorType.EMPLOYEE)));
    setPassword('');
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    if (!isEdit) return undefined;

    setSubmitError(null);

    async function prefill() {
      setIsPrefillLoading(true);
      try {
        const stateEmployee = location?.state?.employee ?? null;
        const stateId = stateEmployee?.id ?? null;
        if (stateEmployee && stateId && String(stateId) === String(employeeId)) {
          if (cancelled) return;
          applyEmployee(stateEmployee);
          return;
        }

        const res = await requestWithMeta(
          `/operator/employees/${encodeURIComponent(employeeId)}`,
          'GET'
        );
        if (cancelled) return;
        if (!res?.ok) {
          setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось загрузить сотрудника');
          return;
        }
        applyEmployee(res?.data?.employee);
      } finally {
        if (!cancelled) setIsPrefillLoading(false);
      }
    }

    prefill();
    return () => {
      cancelled = true;
    };
  }, [isEdit, employeeId, location?.state, requestWithMeta, applyEmployee]);

  const normalizedUsername = normalizeEmployeeUsername(username);
  const passwordOk = isEdit
    ? password.length === 0 || (password.length >= 6 && password.length <= 128)
    : password.length >= 6 && password.length <= 128;
  const isSubmitDisabled =
    loading ||
    isPrefillLoading ||
    name.trim().length === 0 ||
    !normalizedUsername ||
    !passwordOk;

  async function handleSubmit() {
    setSubmitError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setSubmitError('Введите имя');
      return;
    }
    if (!normalizedUsername) {
      setSubmitError('Имя пользователя: от 3 символов, буквы, цифры, точка, _ и -');
      return;
    }
    if (!passwordOk) {
      setSubmitError('Пароль должен быть от 6 до 128 символов');
      return;
    }

    const payload = {
      name: trimmedName,
      username: normalizedUsername,
      typeId: isOwner
        ? OperatorType.OWNER
        : lockSelfType
          ? OperatorType.MANAGER
          : Number(typeId),
    };
    if (!isEdit || password.length > 0) {
      payload.password = password;
    }

    const res = await requestWithMeta(
      isEdit
        ? `/operator/employees/${encodeURIComponent(employeeId)}`
        : '/operator/employees',
      isEdit ? 'PUT' : 'POST',
      payload
    );
    if (!res?.ok) {
      setSubmitError(
        res?.data?.error ||
          res?.data?.message ||
          (isEdit ? 'Не удалось обновить сотрудника' : 'Не удалось создать сотрудника')
      );
      return;
    }

    navigate(listPath, { replace: true });
  }

  return (
    <div className="shop-create-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title={isEdit ? (isOwner ? 'Редактировать владельца' : 'Редактировать сотрудника') : 'Новый сотрудник'}
          subtitle="Логин и пароль для входа через браузер"
          onBack={() => navigate(listPath)}
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="employee-type">
              Тип <b style={{ color: 'red' }}>*</b>
            </label>
            <select
              id="employee-type"
              className="shop-create-input"
              value={typeId}
              onChange={(event) => setTypeId(event.target.value)}
              disabled={isPrefillLoading || isOwner || lockSelfType}
            >
              {isOwner ? (
                <option value={String(OperatorType.OWNER)}>Владелец</option>
              ) : lockSelfType ? (
                <option value={String(OperatorType.MANAGER)}>{operatorTypeLabel(OperatorType.MANAGER)}</option>
              ) : (
                employeeTypeOptions(operatorTypeId).map((type) => (
                  <option key={type.id} value={String(type.id)}>
                    {type.label}
                  </option>
                ))
              )}
            </select>
            {isOwner ? (
              <div className="shop-create-help employee-field-help">Тип владельца нельзя изменить.</div>
            ) : lockSelfType ? (
              <div className="shop-create-help employee-field-help">Нельзя изменить свой тип.</div>
            ) : null}
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="employee-name">
              Имя <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="employee-name"
              className="shop-create-input"
              placeholder="Как обращаться к сотруднику"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="off"
              disabled={isPrefillLoading}
            />
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="employee-username">
              Имя пользователя <b style={{ color: 'red' }}>*</b>
            </label>
            <input
              id="employee-username"
              className="shop-create-input"
              placeholder="например, kassa"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="off"
              disabled={isPrefillLoading}
            />
            <div className="shop-create-help employee-field-help">
              От 3 символов: буквы, цифры, точка, _ и -. Этим логином сотрудник входит в кабинет.
            </div>
          </section>

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="employee-password">
              Пароль {isEdit ? null : <b style={{ color: 'red' }}>*</b>}
            </label>
            <input
              id="employee-password"
              className="shop-create-input"
              type="password"
              placeholder={isEdit ? 'Новый пароль, если нужно сменить' : 'Не короче 6 символов'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              disabled={isPrefillLoading}
            />
            {isEdit ? (
              <div className="shop-create-help employee-field-help">Оставьте пустым, чтобы не менять пароль.</div>
            ) : null}
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={
            loading
              ? isEdit
                ? 'Сохранение…'
                : 'Создание…'
              : isEdit
                ? 'Сохранить'
                : 'Создать сотрудника'
          }
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}

export default UpsertEmployee;
